import React, { Dispatch } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import { sectionHeaderStyle, sectionStyle } from '@/components/organisms/dashboard/style'
import { DateField } from '@/components/molecules/date-field'
import moment from 'moment'
import qs from 'qs'
import { DailyTotal, Cancel, Occ, Price, Repeater } from '@/models/dashboard/dashboard-total'
interface BusinessStatusProps {
  date: Dayjs
  setDate: Dispatch<React.SetStateAction<Dayjs>>
  dailyTotal: DailyTotal
  cancel: Cancel
  occ: Occ
  price: Price
  repeater: Repeater
}

export const BusinessStatus: React.FC<BusinessStatusProps> = ({ date, setDate, dailyTotal, cancel, occ, price, repeater }) => {
  const history = useHistory()
  const { t } = useTranslation()

  const diffStyle = (diff: number) => {
    if (diff === 0) {
      return css({})
    }
    return css({ color: diff > 0 ? '#185AA5' : '#E6483B' })
  }

  const iconPath = (diff: number) => {
    if (diff === 0) {
      return require('@/static/images/icon_keep.svg')
    }
    return diff > 0 ? require('@/static/images/icon_up.svg') : require('@/static/images/icon_down.svg')
  }

  return (
    <div css={sectionStyle}>
      <div css={sectionHeaderStyle}>
        <img
          style={{ cursor: 'pointer' }}
          src={require('@/static/images/arrow_left_gray.svg')}
          onClick={() => {
            setDate(date.add(-1, 'd'))
          }}
        />
        <DateField
          customCss={overWriteDateFieldStyle}
          value={moment(date.format('YYYY-MM-DD'))}
          onChange={date => date && setDate(dayjs(date.format('YYYY-MM-DD')))}
          displayFormat={t('YYYY-MM-DD')}
        />
        <img
          style={{ cursor: 'pointer' }}
          src={require('@/static/images/arrow_right_gray.svg')}
          onClick={() => {
            setDate(date.add(1, 'd'))
          }}
        />
      </div>
      <div css={changePageWrapperStyle}>
        <div
          css={itemBlockHoverStyle}
          onClick={() =>
            history.push({
              pathname: '/accommodation-management',
              search: qs.stringify({
                tabType: 'reservation',
                checkinDateRange: {
                  from: date.format('YYYY-MM-DD'),
                  to: date.format('YYYY-MM-DD'),
                },
              }),
            })
          }
        >
          <div css={iconAndTitleStyle}>
            <img src={require('@/static/images/dashboard/reservation.svg')} />
            <div css={titleStyle}>{t('Reservation')}</div>
          </div>
          <div css={linkStyle}>{dailyTotal?.reservationTotal}</div>
        </div>
        <div
          css={itemBlockHoverStyle}
          onClick={() =>
            history.push({
              pathname: '/accommodation-management',
              search: qs.stringify({
                tabType: 'checkin',
                checkinDateRange: {
                  from: date.format('YYYY-MM-DD'),
                  to: date.format('YYYY-MM-DD'),
                },
              }),
            })
          }
        >
          <div css={iconAndTitleStyle}>
            <img src={require('@/static/images/dashboard/checkin.svg')} />
            <div css={titleStyle}>{t('Checkin')}</div>
          </div>
          <div css={linkStyle}>{dailyTotal?.checkinTotal}</div>
        </div>
        <div
          css={itemBlockHoverStyle}
          onClick={() =>
            history.push({
              pathname: '/accommodation-management',
              search: qs.stringify({ tabType: 'stay' }),
            })
          }
        >
          <div css={iconAndTitleStyle}>
            <img src={require('@/static/images/dashboard/stay.svg')} />
            <div css={titleStyle}>{t('Stay')}</div>
          </div>
          <div css={linkStyle}>{dailyTotal?.stayTotal}</div>
        </div>
        <div
          css={itemBlockHoverStyle}
          onClick={() =>
            history.push({
              pathname: '/accommodation-management',
              search: qs.stringify({
                tabType: 'checkout',
                checkoutDateRange: {
                  from: date.format('YYYY-MM-DD'),
                  to: date.format('YYYY-MM-DD'),
                },
              }),
            })
          }
        >
          <div css={iconAndTitleStyle}>
            <img src={require('@/static/images/dashboard/checkout.svg')} />
            <div css={titleStyle}>{t('Checkout')}</div>
          </div>
          <div css={linkStyle}>{dailyTotal?.checkoutTotal}</div>
        </div>
      </div>
      <div css={changePageWrapperStyle}>
        {/* OCC */}
        <div css={itemBlockStyle}>
          <div css={iconAndTitleStyle}>
            <div css={titleStyle}>{t('DashboardIndicators.OCCRate')}</div>
          </div>
          <div css={linkStyle}>{occ.targetDay}%</div>
          <div css={diffAndPercentStyle}>
            <img src={iconPath(occ.diff)} />
            <span css={diffStyle(occ.diff)}>
              {t('DashboardIndicators.PreviousDay')}: {occ.diff}pt ({occ.previousDay}%)
            </span>
          </div>
        </div>
        {/* 売上 */}
        <div css={itemBlockStyle}>
          <div css={iconAndTitleStyle}>
            <div css={titleStyle}>{t('DashboardIndicators.Price')}</div>
          </div>
          <div css={linkStyle}>¥{price.targetDay.toLocaleString()}</div>
          <div css={diffAndPercentStyle}>
            <img src={iconPath(price.diff)} />
            <span css={diffStyle(price.diff)}>
              {t('DashboardIndicators.PreviousDay')}: {price.diffRate}% (¥{price.previousDay.toLocaleString()})
            </span>
          </div>
        </div>
        {/* キャンセル率 */}
        <div css={itemBlockStyle}>
          <div css={iconAndTitleStyle}>
            <div css={titleStyle}>{t('DashboardIndicators.CancelRate')}</div>
          </div>
          <div css={linkStyle}>{cancel.cancelRate}%</div>
          <div css={diffAndPercentStyle}>
            {t('dashboard.cancel')}{' '}
            {`${cancel.canceledReservationCount} / ${t('DashboardIndicators.TotalReservations')} ${cancel.allReservationCount}`}
          </div>
        </div>
        {/* リピーター率 */}
        <div css={itemBlockStyle}>
          <div css={iconAndTitleStyle}>
            <div css={titleStyle}>{t('DashboardIndicators.RepeaterRate')}</div>
          </div>
          <div css={linkStyle}>{repeater.repeaterRate}%</div>
          <div css={diffAndPercentStyle}>
            {t('DashboardIndicators.repeater')} {repeater.repeaterCount} / {t('DashboardIndicators.TotalCheckIns')}{' '}
            {repeater.reservationCount}
          </div>
        </div>
      </div>
    </div>
  )
}

const changePageWrapperStyle = css({
  display: 'flex',
  height: 192.5,
})

const itemBlockStyle = css({
  borderTop: '1px solid #F2F2F2',
  borderRight: '1px solid #F2F2F2',
  width: '100%',
  padding: '16px 24px 16px 16px',
})

// itemBlockStyle + Hover
const itemBlockHoverStyle = css(itemBlockStyle, {
  cursor: 'pointer',
  '&:hover': {
    background: '#FAFAFA',
  },
})

const iconAndTitleStyle = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: 14,
})

const linkStyle = css({
  fontSize: 30,
  fontWeight: 'bold',
  textAlign: 'right',
  marginTop: 50,
  color: '#272727',
})

const titleStyle = css({
  marginLeft: 12,
  fontWeight: 'bold',
})

const overWriteDateFieldStyle = css({
  'div.SingleDatePicker': {
    '.SingleDatePickerInput': {
      width: '142px',
      border: 'none',
      'div.SingleDatePicker_picker': {
        zIndex: 100,
      },
      '.SingleDatePickerInput_calendarIcon': { display: 'none' },
      'div.DateInput': {
        width: '100%',
        'input.DateInput_input': {
          padding: '5px 0',
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 'bold',
          color: 'rgba(0, 0, 0, 0.65)',
          letterSpacing: '0.8px',
        },
      },
    },
  },
})

const diffAndPercentStyle = css({
  fontSize: 12,
  color: '#808080',
  display: 'flex',
  alignItems: 'center',
  marginTop: 24,
  img: {
    paddingRight: 6,
  },
  justifyContent: 'flex-end',
})
